

























/**
 * External dependencies.
 */
import { Component, Prop, Vue } from 'vue-property-decorator'
import { throttle } from 'lodash'
/**
 * Internal dependencies.
 */
import ProfileResults from '@/components/account/ProfileResults.vue'
import ProfileFavorites from '@/components/account/ProfileFavorites.vue'

import { TYPES } from '@/types'
import { lazyInject } from '@/container'
import { PhotoService } from '@/services/IPhotoService'

import { namespace as profileModule } from '@/store/profile'
import { LOAD_FAVORITES, LOAD_PHOTOS } from '@/store/profile/constants'
import { GalleryType } from '@/store/profile/enums/GalleryType'

@Component({
  components: {
    ProfileResults,
    ProfileFavorites
  }
})
export default class UserGallery extends Vue {
  @Prop({ type: Boolean, required: true }) isMyProfile: boolean
  @Prop({ type: String, required: true }) userId: string
  @Prop({ type: String, required: true }) userName: string
  @Prop(Boolean) autoload: boolean

  @lazyInject(TYPES.PhotoService)
  PhotoService: PhotoService

  photosPage: number = 1
  favoritesPage: number = 1
  isRenderingImages: boolean = false
  bottom: boolean = false
  GALLERY_PHOTOS = GalleryType.GALLERY_PHOTOS
  GALLERY_FAVORITES = GalleryType.GALLERY_FAVORITES

  @profileModule.Getter('isLoadingPhotos') photosLoading: boolean
  @profileModule.Getter('isLoadingFavorites') favoritesLoading: boolean
  @profileModule.Getter getCurrentProfileResultsBatch: any[]
  @profileModule.Getter getCurrentProfileFavoritesBatch: any[]
  @profileModule.Getter getCurrentGalleryType: GalleryType
  @profileModule.Getter areAllPhotosLoaded: boolean
  @profileModule.Getter areAllFavoritesLoaded: boolean

  get selectedComponentProps (): any {
    if (this.getCurrentGalleryType === GalleryType.GALLERY_PHOTOS) {
      return this.getCurrentProfileResultsBatch
    } else if (this.getCurrentGalleryType === GalleryType.GALLERY_FAVORITES) {
      return this.getCurrentProfileFavoritesBatch
    }
  }

  get getTitle () {
    const slogan = this.getCurrentGalleryType === GalleryType.GALLERY_PHOTOS ? `'s Photos` : `'s Favorites`

    return this.userName + slogan
  }

  get isLoading () {
    return this.photosLoading || this.favoritesLoading
  }

  created () {
    // moved photos loading to `mounted()`
  }

  mounted () {
    this.initLoading()

    const throttleFunction = throttle(this.throttledScroll, 500)

    // Attach scroll handler (milliseconds throttle)
    window.addEventListener('scroll', throttleFunction)

    this.$once('hook:destroyed', () => window.removeEventListener('scroll', throttleFunction))
  }

  beforeDestroy () {
    // window.removeEventListener('scroll', throttleFunction)
  }

  @profileModule.Action(LOAD_PHOTOS) loadPhotosFromStore: (data: { userId: string, page: number }) => Promise<any>
  @profileModule.Action(LOAD_FAVORITES) loadFavoritesFromStore: (data: { userId: string, page: number }) => Promise<any>

  throttledScroll () {
    // cross-browser support
    const scrollTop = Math.max(Math.ceil(window.pageYOffset), document.documentElement.scrollTop, document.body.scrollTop)
    let bottomCheck1 = (scrollTop + window.innerHeight) === document.documentElement.offsetHeight - 115
    let bottomCheck2 = (window.innerHeight + window.pageYOffset) >= document.body.offsetHeight - 115

    if ((bottomCheck1 || bottomCheck2)
      && (!this.isLoading && !this.isRenderingImages)
      && !this.bottom
    ) {
      this.bottom = true
      if (this.getCurrentGalleryType === this.GALLERY_PHOTOS) {
        this.loadPhotos()
        this.bottom = false
      } else if (this.getCurrentGalleryType === this.GALLERY_FAVORITES) {
        this.loadFavorites()
        this.bottom = false
      }
    }
  }

  initLoading () {
    if (!this.autoload) {
      // maybe stop initial rendering by sending prop
      // return
    }

    if (this.getCurrentGalleryType === GalleryType.GALLERY_PHOTOS) {
      this.loadPhotos()
    } else if (this.getCurrentGalleryType === GalleryType.GALLERY_FAVORITES) {
      this.loadFavorites()
    }
  }

  loadPhotos () {
    if (this.areAllPhotosLoaded) {
      return
    }

    const { userId, photosPage: page } = this

    void this.loadPhotosFromStore({ userId, page })
      .then(() => this.photosPage++)
  }

  loadFavorites () {
    if (this.areAllFavoritesLoaded) {
      return
    }

    const { userId, favoritesPage: page } = this

    void this.loadFavoritesFromStore({ userId, page })
      .then(() => this.favoritesPage++)
  }
}
